<template>
  <tile class="opc-addon-config additional-template">
    <foldable v-model="isExpanded" :arrow="hasSelectedPlan" :edit="hasSelectedPlan" :title="title">
      <div slot="head" class="opc-zm-base-card__head">
        <div class="opc-zm-base-card__head-left">
          <img :src="logo" class="opc-zm-base-card__logo" :alt="title + ' Icon'" tabindex="-1" />
        </div>
        <div class="opc-zm-base-card__head-right">
          <h2 class="opc-zm-base-card__name text-capitalize">
            {{ title }}
            <i v-if="hasSelectedPlan && !isAdd2CartOrEditFlow" check-icon class="zm-icon-success main-icon" aria-hidden="true" />
          </h2>
          <div v-if="!isExpanded && hasSelectedPlan" slot="collapse" class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
            <ul>
              <li>{{ cartSelectedPlan.cartInfo.selected_number + ' ' + unit }}</li>
              <li>
                <span>{{ $money(cartSelectedPlan.amount) + getUnit(cartSelectedPlan.billing_cycle, 'collapse') }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="divide-line"></div>
      <div class="additional-template__desc">
        <p class="additional-template__usage-desc">{{ description }}</p>
        <p class="additional-template__plan-desc">{{ desc }}</p>
      </div>
      <div class="additional-template__input-number">
        <input-number-with-popover class="additional-template__input-number-selector" ref="userInput" v-model="hosts" size="large"
          controls-position="both" :min="minHosts" :max="maxHosts" @change="changeHosts"
          :label="trackingPlanName + ' Select the number of users'" :decrease-tips="decreaseTips" />
        <span class="additional-template__input-number-desc">{{ unit }}</span>
        <zm-tooltip v-model="zeHubHostTips" :aria-label="'Learn more about ' + unit" placement="bottom">
          <div slot="content" v-html="tips" />
          <i role="button" style="margin-left: 4px; color: #0b5cff" class="zm-icon-info-outline" />
        </zm-tooltip>
      </div>
      <alert v-show="alertMessage.message" class="additional-template__alert" :type="alertMessage.type">
        <span v-html="alertMessage.message" />
      </alert>
      <subscription-cycle :value="selectedPlanCode" :options="billingCycleOptions" @change="onPlanSelect" />
      <div class="opc-zm-base-card__main__footer" v-if="showRemove">
        <zm-button type="link" ghost @click="removePlanByKey()" :aria-label="'remove ' + title + ' plan'">
          {{ $t('billing.common.remove') }}
        </zm-button>
      </div>
      <preview-payment class="additional-template__preview-payment" />
    </foldable>
  </tile>
</template>
<script>
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import SubscriptionCycle from '@/components/subscription-cycle.vue';
import plansMixin from '@/plans/mx/plan';
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
import { planTypes, trackingPlanTypes, BILLING_CYCLE } from '@/common/constants';
import { flowTypes } from '@/common/service/page-config.js';
import { get } from 'lodash';
import Alert from '@/components/zoom-ui-plus/alert/alert.vue';
import { pageConfig } from '@/common/service/page-config';
import { dateTimeFormat } from '@/utils/dateformat.js';
import PreviewPayment from '@/views/checkout/payment/preview-payment';

export default {
  name: 'Basic',
  components: { PreviewPayment, Tile, Foldable, InputNumberWithPopover, SubscriptionCycle, Alert },
  mixins: [plansMixin],
  props: {
    title: String,
    logo: String,
    description: String,
    desc: String,
    planType: String,
  },
  data() {
    return {
      zeHubHostTips: false,
      isExpanded: true,
      selectedPlanCode: '',
      hosts: 1,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'basicConfig', 'currentEditPlan', 'assignInfo', 'boughtPlans']),
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
    }),
    unit() {
      return this.planType === planTypes.ADDITIONAL_HOST ? this.$t('billing.events.addon.hub_hosts_unit') : this.$t('billing.events.addon.co_editors_unit');
    },
    isEditFlow() {
      return this.$context.flow === flowTypes.edit && this.$context.is.editPage;
    },
    hasSelectedPlan() {
      return !!this.selectedPlanCode;
    },
    minHosts() {
      if (this.isEditFlow && this.basicConfig.downSellData.disableDownSell || !this.isStandardPrice) {
        return get(this.currentEditPlan, 'cartInfo.selected_number', 0);
      }
      return this.cartSelectedPlan?.productInfo?.min || 1;
    },
    maxHosts() {
      return this.cartSelectedPlan?.productInfo?.max || 20;
    },
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.planType && p.isCurrentCategory);
    },
    getUnit() {
      const units = {
        collapse: {
          [planTypes.ADDITIONAL_HOST]: {
            monthly: this.$t('billing.events.addon.monthly_hub_host'),
            annual: this.$t('billing.events.addon.annual_hub_host'),
            quarterly: this.$t('billing.events.addon.quarterly_hub_host'),
            two_years: this.$t('billing.events.addon.two_years_hub_host'),
            three_years: this.$t('billing.events.addon.three_years_hub_host'),
          },
          [planTypes.ADDITIONAL_CO_EDITOR]: {
            monthly: this.$t('billing.events.addon.monthly_co_editor'),
            annual: this.$t('billing.events.addon.annual_co_editor'),
            quarterly: this.$t('billing.events.addon.quarterly_co_editor'),
            two_years: this.$t('billing.events.addon.two_years_co_editor'),
            three_years: this.$t('billing.events.addon.three_years_co_editor'),
          },
        },
        cycle: {
          [planTypes.ADDITIONAL_HOST]: {
            monthly: this.$t('billing.events.addon.monthly_host'),
            annual: this.$t('billing.events.addon.annual_host'),
            quarterly: this.$t('billing.events.addon.quarterly_host'),
            two_years: this.$t('billing.events.addon.two_years_host'),
            three_years: this.$t('billing.events.addon.three_years_host'),
          },
          [planTypes.ADDITIONAL_CO_EDITOR]: {
            monthly: this.$t('billing.events.addon.monthly_co_editor'),
            annual: this.$t('billing.events.addon.annual_co_editor'),
            quarterly: this.$t('billing.events.addon.quarterly_co_editor'),
            two_years: this.$t('billing.events.addon.two_years_co_editor'),
            three_years: this.$t('billing.events.addon.three_years_co_editor'),
          },
        },
      };
      // type = ['collapse', 'cycle']
      return (cycle, type = 'cycle', planType = this.planType) => units[type][planType][cycle];
    },
    billingCycleOptions() {
      const monthly = this.planList.find((p) => p.billing_cycle === BILLING_CYCLE.MONTHLY);
      const annual = this.planList.find((p) => p.billing_cycle === BILLING_CYCLE.ANNUAL);

      const addonPlanName = this.title;
      return this.planList.map((p) => {
        const dependPlanList = p.billingCycleDependBasePlanTypeList;
        // based on zoom sass
        let isZoomSass = this.boughtPlans.filter(p => !p.isPPA && dependPlanList.includes(p.type))
          .sort((a, b) => a.cycleValue - b.cycleValue)[0];
        if (!isZoomSass) {
          isZoomSass = this.getSelectedPlans.find(p => dependPlanList.includes(p.type))
        }
        const basePlanName = isZoomSass && this.$t(isZoomSass.typeNameKey);
        const isBulkLicenseAccountDisabled =
          this.isEditFlow &&
          this.basicConfig.isBulkLicenseAccount &&
          get(this.currentEditPlan, ['billing_cycle'], '') !== p.billing_cycle;
        const bulkTooltip = isBulkLicenseAccountDisabled ? this.$t('billing.error.buld_acount_license') : '';
        const isDisabledExceedCycle = p.cycleValue > isZoomSass?.cycleValue;
        const isDisabled = isDisabledExceedCycle || isBulkLicenseAccountDisabled;
        return Object.assign({}, p, {
          disabled: isDisabled,
          disabledTooltip: isBulkLicenseAccountDisabled ? bulkTooltip :
            isDisabledExceedCycle ? this.$t('billing.planform.annual_addon_disable_tooltip', [basePlanName, addonPlanName, basePlanName]) : '',
          label: p.plan_code,
          cycle: this.$t(`billing.cycle.${p.billing_cycle}`),
          price: this.$money(p.amount, { forceDiff: true }),
          unit: this.getUnit(p.billing_cycle),
          cyclePrice: p.billing_cycle === BILLING_CYCLE.ANNUAL ? {
            annualPrice: annual?.amount,
            monthlyPrice: monthly?.amount
          } : null
        });
      });
    },
    trackingPlanKey() {
      return get(trackingPlanTypes, get(planTypes, this.planType));
    },
    cartSelectedPlan() {
      return get(
        this.getSelectedPlans.filter((p) => p.type === this.planType),
        [0],
      );
    },
    showIcon() {
      return this.showRemove && this.hasSelectedPlan;
    },
    showRemove() {
      return this.$context.is.buyNow || this.$context.is.basic2pro;
    },
    decreaseTips() {
      const downSellData = this.basicConfig.downSellData;
      const previousHosts = get(this.currentEditPlan, 'cartInfo.selected_number', 0);
      if (this.isEditFlow && this.hosts === previousHosts) {
        if (downSellData.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }

        return this.forNonStandardPriceTips;
      }

      return '';
    },
    tips() {
      return this.planType === planTypes.ADDITIONAL_HOST
        ? this.$t('billing.events.addon.ze_hub_hosts_tips')
        : this.$t('billing.events.addon.zs_co_editors_tips');
    },
    alertMessage() {
      if (this.hosts >= this.maxHosts) {
        return {
          message: this.$t('billing.events.addon.maximun_alert_message', [this.unit]),
          type: 'warning'
        }
      }
      // downgrade case
      if (this.isEditFlow) {
        const { assignLicenseQuantity = 0, planExpireTime } = get(this.assignInfo, pageConfig.oldPlanCode, {});

        if (assignLicenseQuantity - this.hosts > 0) {
          const needAssignQuantity = assignLicenseQuantity - this.hosts;
          return {
            message: this.$t('billing.error.downgrade_alert_for_ze_addon', [
              needAssignQuantity,
              this.planType === planTypes.ADDITIONAL_HOST
                ? this.$t('billing.events.addon.hosts')
                : this.$t('billing.events.addon.co_editors'),
              dateTimeFormat(planExpireTime),
            ]),
            type: 'info',
          };
        }
      }
      return {};
    },
    trackingPlanName() {
      return this.$t('billing.planform.title.' + this.trackingPlanKey);
    }
  },
  mounted() {
    this.$context.$on('collapse-plan-card', this.collapseCard);
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'removePlan']),
    ...mapActions(['switchSelectPlan']),
    onPlanSelect(newPlanCode) {
      const oldPlanCode = this.selectedPlanCode;

      if (this.selectedPlanCode !== newPlanCode) {
        if (oldPlanCode) {
          this.switchSelectPlan([oldPlanCode, newPlanCode, undefined]);
        } else {
          let quantity = this.hosts;
          if (quantity) {
            quantity = quantity <= this.maxHosts ? quantity : this.maxHosts;
          } else {
            quantity = this.minHosts;
          }
          this.hosts = quantity;
          this.updateSelectPlan({
            plan_code: newPlanCode,
            cartInfo: { selected_number: quantity },
          });
        }
        this.selectedPlanCode = newPlanCode;
        // event tracking
        const billingCycle = this.cartSelectedPlan?.billing_cycle;
        billingCycle &&
          this.$tracking.addOnBuyInteraction(
            `${this.trackingPlanKey} billing cycle`,
            this.$t(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
          );
      }
    },
    changeHosts(newHosts) {
      const planCode = this.selectedPlanCode;
      const oldHosts = this.hosts;

      if (planCode) {
        this.updateSelectPlan({
          plan_code: planCode,
          cartInfo: { selected_number: newHosts },
        });
      }

      this.hosts = newHosts;
      // event tracking
      planCode &&
        this.$tracking.addOnBuyInteraction(
          `${this.trackingPlanKey} hosts`,
          `${newHosts > oldHosts ? 'increase' : 'decrease'} - ${newHosts}`,
        );
    },
    // For A11Y
    getFirstFocusableControl() {
      return Promise.resolve(this.$refs.userInput);
    },
    collapseCard() {
      this.isExpanded = false;
    },
  },
  watch: {
    zeHubHostTips: {
      handler(v) {
        v && this.$tracking.buyInteraction('zoom events add-ons',
          `${this.trackingPlanKey} - tooltip`);
      }
    },
    cartSelectedPlan: {
      immediate: true,
      handler(plan) {
        this.selectedPlanCode = plan?.plan_code ?? '';
        this.hosts = plan?.cartInfo?.selected_number ?? this.minHosts;
      },
    },
    isExpanded: {
      handler(val) {
        this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} accordion`, val ? 'expand' : 'collapse');
      },
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/zoom-card.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.additional-template {
  .opc-zm-base-card__name {
    @include media(mobile) {
      display: block;
      padding-right: 60px;
    }
  }

  &__alert {
    margin-top: 12px;
  }

  &__usage-desc,
  &__plan-desc {
    color: #666487;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
  }

  &__plan-desc {
    margin-top: 24px;
  }

  &__input-number {
    margin-top: 24px;

    &-desc {
      margin-left: 8px;
      color: #00053d;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 120%;
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }
}
</style>
